import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Modal } from "Components/_Shared/BlModal";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useId } from "react";

type DialogSize = "base" | "sm" | "md" | "lg";

type Props = {
  title: string;
  subtitle: ReactNode;
  isOpen: boolean;
  // without onClose the dialog cannot be closed at all (no cross, no close button, Esc and backdrop do nothing)
  onClose?: () => void;
  primaryAction: ReactNode;
  secondaryAction?: ReactNode;
  content?: ReactNode;
  size?: DialogSize;
  closeLabel?: string;
  // keeps the dialog open until the pending action settles, so its result is not lost behind a closed dialog
  isPending?: boolean;
};

export const DialogModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  primaryAction,
  content,
  secondaryAction,
  size = "md",
  closeLabel,
  isPending = false,
}: Props) => {
  const { t } = useResource();
  const theme = useTheme();
  const titleId = useId();

  const isClosable = onClose !== undefined;

  const handleClose = () => {
    if (!isPending) onClose?.();
  };

  const widthBySizeMap: Record<DialogSize, string | object> = {
    base: { xs: "auto", md: theme.spacing(90) },
    sm: { xs: "auto", md: theme.spacing(120) },
    md: { xs: "auto", md: theme.spacing(150) },
    lg: { xs: "auto", md: theme.spacing(200) },
  };

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby={titleId}>
      <Flex
        sx={{
          width: widthBySizeMap[size],
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: 3,
          borderRadius: Radius.XXXL,
          p: Spacing.XXL,
          background: "white",
        }}
      >
        <Flex justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography id={titleId} variant="h2">
            {title}
          </Typography>
          {isClosable && (
            <IconButton onClick={handleClose} disabled={isPending} aria-label={t(Resources.Common.Close)}>
              <CloseIcon />
            </IconButton>
          )}
        </Flex>
        <Stack spacing={Spacing.XXL} sx={{ overflowY: "auto", flex: 1, mt: Spacing.XXL, pr: Spacing.L }}>
          <Typography variant="body1" component="div">
            {subtitle}
          </Typography>
          {content && content}
          <Flex justifyContent="space-between">
            <Box>
              {isClosable && (
                <Button color="secondary" disabled={isPending} onClick={handleClose}>
                  {closeLabel ?? t(Resources.Common.Close)}
                </Button>
              )}
            </Box>
            <Flex gap={Spacing.L}>
              <>
                {secondaryAction && secondaryAction}
                {primaryAction}
              </>
            </Flex>
          </Flex>
        </Stack>
      </Flex>
    </Modal>
  );
};
